import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConvexAuth, useQuery } from "convex/react";
import { api } from '../../../../convex/_generated/api';
import { SignInForm } from '../../../SignInForm';

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  color: 'primary' | 'secondary' | 'accent' | 'highlight';
}

function FeatureCard({ icon, title, description, color }: FeatureCardProps) {
  const colorClasses = {
    primary: 'bg-primary text-white',
    secondary: 'bg-secondary text-white',
    accent: 'bg-accent text-white',
    highlight: 'bg-warning text-black',
  };

  return (
    <div className={`comic-panel p-6 ${colorClasses[color]} transition-all duration-300 hover:shadow-pop-lg`}>
      <div className="flex items-center justify-center w-16 h-16 mb-4 mx-auto bg-white border-pop-medium rounded-full">
        {icon}
      </div>
      <h3 className="text-pop-extra-bold text-lg mb-3 text-center">{title}</h3>
      <p className="text-sm leading-relaxed text-center opacity-90">{description}</p>
    </div>
  );
}

function HeroSection({ onShowSignIn }: { onShowSignIn: () => void }) {
  const navigate = useNavigate();
  const { isAuthenticated } = useConvexAuth();

  const handleGetStarted = () => {
    if (isAuthenticated) {
      void navigate('/');
    } else {
      onShowSignIn();
    }
  };

  return (
    <section className="py-20 px-4 bg-ben-day-light relative overflow-hidden">
      <div className="max-w-4xl mx-auto text-center relative z-10">
        <div className="comic-panel bg-white p-8 mb-8">
          <h1 className="text-4xl md:text-6xl text-pop-black text-gray-800 mb-6 leading-tight">
            <span className="text-comic-emphasis inline-block transform -rotate-1">Organize</span> Your
            <br />
            <span className="text-primary text-pop-black"> Creative References</span>
            <br />
            <span className="text-secondary text-pop-black">Effortlessly!</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed font-medium">
            The only visual reference tool built specifically for creative professionals. 
            <span className="text-comic-emphasis mx-2">AI-powered organization</span> 
            meets lightning-fast search and seamless workflow integration!
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <button
            onClick={handleGetStarted}
            className="btn btn-primary btn-lg"
          >
            {isAuthenticated ? '🎮 Go to App' : '🚀 Get Started Free'}
          </button>
          {!isAuthenticated && (
            <button
              onClick={onShowSignIn}
              className="btn btn-outline btn-secondary btn-lg"
            >
              👋 Sign In
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

function FeaturesSection() {
  const features = [
    {
      icon: (
        <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      title: "AI-Powered Creative Intelligence",
      description: "Unlike generic tools, our AI understands design context and creative intent. Automatically extract colors, styles, and design patterns while generating intelligent tags that actually help your creative workflow.",
      color: 'primary' as const,
    },
    {
      icon: (
        <svg className="w-8 h-8 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      ),
      title: "Lightning-Fast Visual Discovery",
      description: "Find inspiration in milliseconds, not minutes. Our advanced search understands visual similarity, color palettes, and design styles - making reference discovery 10x faster than traditional methods.",
      color: 'secondary' as const,
    },
    {
      icon: (
        <svg className="w-8 h-8 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
        </svg>
      ),
      title: "Seamless Creative Workflow Integration",
      description: "Built specifically for designers and creatives. Share directly from any app, access via PWA on any device, and integrate with your existing design tools without disrupting your creative flow.",
      color: 'accent' as const,
    },
    {
      icon: (
        <svg className="w-8 h-8 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: "AI-Generated Creative Prompts",
      description: "Transform references into actionable creative instructions. Generate detailed prompts for AI art tools, extract design principles, and create comprehensive style guides from your visual references.",
      color: 'highlight' as const,
    },
  ];

  return (
    <section className="py-20 px-4 bg-white relative">
      {/* Section divider */}
      <div className="section-divider-pop"></div>
      
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <div className="comic-panel p-8 inline-block mb-8">
            <h2 className="text-3xl md:text-4xl text-gray-800 mb-4">
              Everything You Need to Stay 
              <span className="text-comic-emphasis mx-2">Inspired!</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto font-medium">
              Powerful features designed to streamline your creative workflow and help you organize inspiration efficiently.
            </p>
          </div>
        </div>
        
        <div className="pop-grid">
          {features.map((feature, index) => (
            <FeatureCard key={index} {...feature} />
          ))}
        </div>
      </div>
    </section>
  );
}

function CopyableCommand({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      // Ignore; user can still select and copy manually.
    }
  };

  return (
    <div className="comic-panel bg-base-100 p-4 border-2 border-gray-800">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="text-sm font-bold text-gray-800 uppercase tracking-wide">{label}</div>
        <button
          type="button"
          onClick={() => void handleCopy()}
          className={`btn btn-sm ${copied ? "btn-success" : "btn-outline btn-secondary"}`}
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <code className="block text-sm font-mono bg-white border-2 border-gray-800 rounded-lg p-3 break-all select-all">
        {value}
      </code>
    </div>
  );
}

function AgentSkillSection({ onShowSignIn }: { onShowSignIn: () => void }) {
  const navigate = useNavigate();
  const { isAuthenticated } = useConvexAuth();

  const handleGenerateToken = () => {
    if (isAuthenticated) {
      void navigate("/settings");
    } else {
      onShowSignIn();
    }
  };

  return (
    <section className="py-20 px-4 bg-ben-day-light relative overflow-hidden">
      <div className="section-divider-pop"></div>

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-12">
          <div className="comic-panel bg-white p-8 inline-block">
            <h2 className="text-3xl md:text-4xl text-gray-800 mb-3">
              🤖 Use Refstash Inside Your{" "}
              <span className="text-comic-emphasis">Coding Agent</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto font-medium">
              Bring your reference library into Claude Code / Cursor to explore UI patterns, generate briefs, and stay
              consistent — without leaving your editor.
            </p>

            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <button type="button" onClick={handleGenerateToken} className="btn btn-primary btn-lg">
                🔑 Generate API Token
              </button>
              <a
                className="btn btn-outline btn-secondary btn-lg"
                href="https://github.com/gtrias/refstash-skill"
                target="_blank"
                rel="noreferrer"
              >
                GitHub ↗
              </a>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="comic-panel bg-warning p-6 border-2 border-gray-800">
            <h3 className="text-pop-extra-bold text-black mb-3">1) Token</h3>
            <p className="text-black opacity-90 mb-4">
              Create a token in Settings and set it as <code className="px-1 bg-white border border-gray-800 rounded">REFSTASH_API_TOKEN</code>.
              Tokens are revocable anytime.
            </p>
            <button type="button" onClick={handleGenerateToken} className="btn btn-sm btn-neutral">
              Open Settings
            </button>
          </div>

          <div className="space-y-4">
            <CopyableCommand label="2) Install skill" value="npx skills add gtrias/refstash-skill" />
            <div className="comic-panel bg-base-100 p-4 border-2 border-gray-800">
              <div className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-2">Privacy</div>
              <ul className="text-sm text-gray-700 space-y-1 font-medium">
                <li>• AI runs in your agent runner</li>
                <li>• Refstash only serves your reference data</li>
                <li>• Revoke tokens anytime in Settings</li>
              </ul>
            </div>
          </div>

          <div className="space-y-4">
            <CopyableCommand
              label="3) Try it"
              value='Use `refstash.listProjects` to list my projects, then use `refstash.search` with the projectId and limit 10. Return ids + titles + tags.'
            />
            <CopyableCommand
              label="Search example"
              value='Use `refstash.search` with projectId "<proj_xxx>", tags ["dashboard","navigation"], query "table", limit 10.'
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function BenefitsSection() {
  return (
    <section className="py-20 px-4 bg-ben-day-medium relative">
      {/* Pop art background elements */}
      <div className="absolute top-20 right-10 w-32 h-32 bg-primary border-pop-thick transform rotate-45 opacity-10"></div>
      <div className="absolute bottom-20 left-10 w-24 h-24 bg-accent border-pop-thick rounded-full opacity-10"></div>
      
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="comic-panel bg-white p-8 mb-8">
              <h2 className="text-3xl md:text-4xl text-pop-black text-gray-800 mb-6">
                Transform How You 
                <span className="text-comic-emphasis mx-2">Collect & Use</span>
                Design References
              </h2>
            </div>
            
            <div className="space-y-6">
              <div className="comic-panel bg-success p-6 flex items-start gap-4">
                <div className="w-12 h-12 bg-white border-pop-medium rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-pop-bold text-white mb-2">Save Time with Smart Organization</h3>
                  <p className="text-white opacity-90">No more manual tagging or lost references. AI automatically organizes everything for you!</p>
                </div>
              </div>
              
              <div className="comic-panel bg-info p-6 flex items-start gap-4">
                <div className="w-12 h-12 bg-white border-pop-medium rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-info" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-pop-bold text-white mb-2">Find Inspiration Instantly</h3>
                  <p className="text-white opacity-90">Advanced search and filtering helps you find the perfect reference in seconds!</p>
                </div>
              </div>
              
              <div className="comic-panel bg-warning p-6 flex items-start gap-4">
                <div className="w-12 h-12 bg-black border-pop-medium rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-pop-bold text-black mb-2">Create Anywhere</h3>
                  <p className="text-black opacity-90">PWA technology means you can access and add to your collection from any device!</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="comic-panel bg-white p-8">
            <div className="alert alert-speech-bubble bg-primary text-white mb-6">
              <div className="text-sm mb-2">📱 <span className="text-pop-bold">Screenshot shared from mobile app</span></div>
            </div>
            
            <div className="comic-panel bg-warning p-6 text-black">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">✨</span>
                  <span className="text-pop-bold">AI detected:</span>
                  <span>Mobile app login screen, iOS interface, blue theme</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🏷️</span>
                  <span className="text-pop-bold">Auto-tagged:</span>
                  <div className="flex gap-2 flex-wrap">
                    <span className="badge badge-primary">#mobile</span>
                    <span className="badge badge-secondary">#login</span>
                    <span className="badge badge-accent">#iOS</span>
                    <span className="badge badge-info">#blue-theme</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-center text-sm text-gray-600 mt-6 font-medium">
              <span className="text-comic-emphasis">See how AI automatically organizes your references!</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PopularReferencesSection() {
  const navigate = useNavigate();
  
  // Fetch popular public references
  const popularPublicReferences = useQuery(
    api.publicDirectory.getPublicReferences,
    {
      filters: {
        sortBy: 'popular',
      },
      limit: 6
    }
  );

  if (!popularPublicReferences || popularPublicReferences.references.length === 0) {
    return null;
  }

  return (
    <section className="py-20 px-4 bg-yellow-300 relative overflow-hidden">
      {/* Pop art background elements */}
      <div className="absolute top-10 right-10 w-32 h-32 bg-white border-pop-thick transform rotate-12 opacity-20"></div>
      <div className="absolute bottom-10 left-10 w-24 h-24 bg-primary border-pop-thick rounded-full opacity-20"></div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-12">
          <div className="comic-panel bg-white p-6 inline-block mb-6">
            <h2 className="text-3xl md:text-4xl text-pop-black text-gray-800 mb-3">
              🔥 Popular Public References
            </h2>
            <p className="text-lg text-gray-700 font-medium">
              Discover what the creative community is loving
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4 mb-8">
          {popularPublicReferences.references.slice(0, 6).map((ref) => (
            <div
              key={ref._id}
              onClick={() => navigate(`/public/reference/${ref._id}`)}
              className="card bg-white shadow-comic border-2 border-gray-800 hover:shadow-comic-lg transition-shadow cursor-pointer"
            >
              {ref.imageUrl && (
                <figure className="px-2 pt-2">
                  <img
                    src={ref.imageUrl}
                    alt={ref.title}
                    className="rounded-lg w-full h-24 sm:h-32 object-cover border-2 border-gray-800"
                  />
                </figure>
              )}
              <div className="card-body p-2 sm:p-3">
                <h3 className="card-title text-xs sm:text-sm text-gray-900 line-clamp-2 mb-1">
                  {ref.title}
                </h3>
                <div className="flex items-center justify-between text-xs">
                  <div className="badge badge-secondary badge-xs">{ref.type}</div>
                  <div className="flex gap-1 text-gray-600">
                    <span>👁 {ref.publicMetadata?.views || 0}</span>
                    <span>❤️ {ref.publicMetadata?.likes || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="text-center">
          <button
            type="button"
            onClick={() => navigate('/public')}
            className="btn btn-primary btn-lg shadow-comic border-2 border-gray-800"
          >
            View All Public References →
          </button>
        </div>
      </div>
    </section>
  );
}

function CTASection({ onShowSignIn }: { onShowSignIn: () => void }) {
  const navigate = useNavigate();
  const { isAuthenticated } = useConvexAuth();

  const handleStartNow = () => {
    if (isAuthenticated) {
      void navigate('/');
    } else {
      onShowSignIn();
    }
  };

  return (
    <section className="py-20 px-4 bg-primary relative overflow-hidden">
      {/* Pop art background elements */}
      <div className="absolute top-10 left-10 w-40 h-40 bg-warning border-pop-thick transform rotate-12 opacity-20"></div>
      <div className="absolute bottom-10 right-10 w-32 h-32 bg-accent border-pop-thick rounded-full opacity-20"></div>
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-secondary border-pop-thick transform rotate-45 opacity-10"></div>
      
      <div className="max-w-4xl mx-auto text-center relative z-10">
        <div className="comic-panel bg-white p-8 mb-8">
          <h2 className="text-3xl md:text-4xl text-pop-black text-gray-800 mb-4">
            Ready to Organize Your 
            <span className="text-comic-emphasis mx-2">Creative References?</span>
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto font-medium">
            Join thousands of designers, developers, and creatives who use 
            <span className="text-comic-emphasis mx-2">REFSTASH</span> 
            to stay organized and find inspiration quickly!
          </p>
        </div>
        
        <button
          onClick={handleStartNow}
          className="btn btn-warning btn-lg"
        >
          {isAuthenticated ? '🎮 Go to App' : '🎨 Start Using REFSTASH Now!'}
        </button>
      </div>
    </section>
  );
}

export function LandingPage() {
  const [showSignIn, setShowSignIn] = useState(false);

  const handleShowSignIn = () => {
    setShowSignIn(true);
  };

  const handleCloseSignIn = () => {
    setShowSignIn(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Pop art background elements */}
      <div className="absolute top-10 left-10 w-20 h-20 bg-primary border-pop-thick transform rotate-12 opacity-20"></div>
      <div className="absolute top-32 right-20 w-16 h-16 bg-accent border-pop-thick rounded-full opacity-20"></div>
      <div className="absolute bottom-20 left-1/4 w-24 h-24 bg-warning border-pop-thick transform -rotate-12 opacity-20"></div>
      
      <HeroSection onShowSignIn={handleShowSignIn} />
      <FeaturesSection />
      <AgentSkillSection onShowSignIn={handleShowSignIn} />
      <BenefitsSection />
      <PopularReferencesSection />
      <CTASection onShowSignIn={handleShowSignIn} />
      
      {/* Sign In Modal */}
      {showSignIn && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
          style={{ zIndex: 9999 }}
          onClick={(e) => {
            // Close modal if clicking on backdrop
            if (e.target === e.currentTarget) {
              handleCloseSignIn();
            }
          }}
        >
          <div className="comic-panel bg-white max-w-md w-full max-h-[90vh] overflow-y-auto p-6 relative">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl text-pop-extra-bold text-gray-800">
                <span className="text-comic-emphasis">Welcome to REFSTASH!</span>
              </h2>
              <button
                onClick={handleCloseSignIn}
                className="btn btn-ghost btn-sm"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <SignInForm />
          </div>
        </div>
      )}
    </div>
  );
} 
